import {
  GraphQLID,
  GraphQLNonNull,
  GraphQLInputObjectType,
  GraphQLString,
  GraphQLBoolean,
  GraphQLList,
  GraphQLEnumType
} from 'graphql'
import { Context } from '../shared'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import { ContrastGraphQLFieldConfig } from '..'
import gql from 'graphql-tag'
import { Connector } from '../../../api/connector'

interface CreateRuleExclusionRulesData {
  readonly all: boolean
  readonly specificRuleUUIDs: string[]
}

interface CommonArgs {
  readonly organizationID: string
  readonly applicationID: string
  readonly eventUUID: string
  readonly suppressEvents: boolean
  readonly assessRulesInfo: CreateRuleExclusionRulesData
  readonly protectRulesInfo: CreateRuleExclusionRulesData
  readonly exclusionName: string
}

type InputType = 'PARAMETER' | 'HEADER' | 'QUERYSTRING' | 'BODY' | 'COOKIE'

const inputTypeEnum = new GraphQLEnumType({
  name: 'ExclusionInputType',
  values: {
    PARAMETER: { value: 'PARAMETER' },
    HEADER: { value: 'HEADER' },
    QUERYSTRING: { value: 'QUERYSTRING' },
    BODY: { value: 'BODY' },
    COOKIE: { value: 'COOKIE' }
  }
})

const rulesDataType = new GraphQLInputObjectType({
  name: 'CreateRuleExclusionRulesData',
  fields: () => ({
    all: {
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    specificRuleUUIDs: {
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    }
  })
})

const common = {
  organizationID: {
    description: 'The ID of the organization that the exclusion belongs to',
    type: new GraphQLNonNull(GraphQLID)
  },
  applicationID: {
    description: 'The application ID',
    type: new GraphQLNonNull(GraphQLString)
  },
  eventUUID: {
    type: new GraphQLNonNull(GraphQLString)
  },
  suppressEvents: {
    description: 'Suppress attack events after the exclusion is created',
    type: new GraphQLNonNull(GraphQLBoolean)
  },
  assessRulesInfo: {
    type: new GraphQLNonNull(rulesDataType)
  },
  protectRulesInfo: {
    type: new GraphQLNonNull(rulesDataType)
  },
  exclusionName: {
    type: new GraphQLNonNull(GraphQLString)
  }
}

const inputTypeArgs = new GraphQLInputObjectType({
  name: 'CreateRuleExclusionInputTypeArgs',
  fields: () => ({
    ...common,
    inputName: {
      description: 'Required for inputType of PARAMETER|HEADER|COOKIE',
      type: GraphQLString
    },
    inputType: {
      type: new GraphQLNonNull(inputTypeEnum)
    },
    urls: {
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    }
  })
})

const codeTypeArgs = new GraphQLInputObjectType({
  name: 'CreateRuleExclusionCodeTypeArgs',
  fields: () => ({
    ...common,
    codes: {
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    }
  })
})

const urlTypeArgs = new GraphQLInputObjectType({
  name: 'CreateRuleExclusionURLTypeArgs',
  fields: () => ({
    ...common,
    urls: {
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    }
  })
})

export const mutationCreateRuleExclusionInputType = gql`
  mutation CreateRuleExclusionInputType(
    $exclusionName: String!
    $organizationID: ID!
    $applicationID: String!
    $eventUUID: String!
    $suppressEvents: Boolean!
    $assessRules: CreateRuleExclusionRulesData!
    $protectRules: CreateRuleExclusionRulesData!
    $inputType: ExclusionInputType!
    $inputName: String!
  ) {
    createRuleExclusionInputType(
      input: {
        exclusionName: $exclusionName
        organizationID: $organizationID
        applicationID: $applicationID
        eventUUID: $eventUUID
        suppressEvents: $suppressEvents
        assessRules: $assessRules
        protectRules: $protectRules
        inputName: $inputName
        inputType: $inputType
      }
    ) {
      _placeholder
    }
  }
`

const descriptionPrefix =
  'Create rule exclusion for application and attack event:'

interface CreateRuleExclusionInputTypeArgs extends CommonArgs {
  readonly inputType: InputType
  readonly inputName?: string
  readonly urls: string[]
}

export const createRuleExclusionInputType: ContrastGraphQLFieldConfig<
  any,
  Context,
  {
    readonly input: CreateRuleExclusionInputTypeArgs
  },
  GenericMutationResponse
> = {
  description: `${descriptionPrefix}: input type`,
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: {
    input: {
      type: inputTypeArgs
    }
  },
  resolve: async (_source, args, { connector }) => {
    const {
      input: {
        organizationID,
        applicationID,
        suppressEvents,
        exclusionName,
        eventUUID,
        assessRulesInfo,
        protectRulesInfo,
        inputType,
        inputName,
        urls
      }
    } = args

    return sendRequests(
      connector,
      organizationID,
      applicationID,
      suppressEvents,
      'input',
      exclusionName,
      eventUUID,
      assessRulesInfo,
      protectRulesInfo,
      {
        urls,
        input_type: inputType,
        input_name: inputName
      }
    )
  }
}

export const mutationCreateRuleExclusionCodeType = gql`
  mutation CreateRuleExclusionCodeType(
    $exclusionName: String!
    $organizationID: ID!
    $applicationID: String!
    $eventUUID: String!
    $suppressEvents: Boolean!
    $assessRules: [String]!
    $protectRules: [String]!
    $codes: [String]!
  ) {
    createRuleExclusionCodeType(
      input: {
        exclusionName: $exclusionName
        organizationID: $organizationID
        applicationID: $applicationID
        eventUUID: $eventUUID
        suppressEvents: $suppressEvents
        assessRules: $assessRules
        protectRules: $protectRules
        codes: $codes
      }
    ) {
      _placeholder
    }
  }
`

interface CreateRuleExclusionCodeTypeArgs extends CommonArgs {
  readonly codes: string[]
}

export const createRuleExclusionCodeType: ContrastGraphQLFieldConfig<
  any,
  Context,
  {
    readonly input: CreateRuleExclusionCodeTypeArgs
  },
  GenericMutationResponse
> = {
  description: `${descriptionPrefix}: code type`,
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: {
    input: {
      type: codeTypeArgs
    }
  },
  resolve: async (_source, args, { connector }) => {
    const {
      input: {
        organizationID,
        applicationID,
        suppressEvents,
        exclusionName,
        eventUUID,
        assessRulesInfo,
        protectRulesInfo,
        codes
      }
    } = args

    return sendRequests(
      connector,
      organizationID,
      applicationID,
      suppressEvents,
      'code',
      exclusionName,
      eventUUID,
      assessRulesInfo,
      protectRulesInfo,
      {
        codes
      }
    )
  }
}

export const mutationCreateRuleExclusionURLType = gql`
  mutation CreateRuleExclusionURLType(
    $exclusionName: String!
    $organizationID: ID!
    $applicationID: String!
    $eventUUID: String!
    $suppressEvents: Boolean!
    $assessRules: [String]!
    $protectRules: [String]!
    $urls: [String]!
  ) {
    createRuleExclusionURLType(
      input: {
        exclusionName: $exclusionName
        organizationID: $organizationID
        applicationID: $applicationID
        eventUUID: $eventUUID
        suppressEvents: $suppressEvents
        assessRules: $assessRules
        protectRules: $protectRules
        urls: $urls
      }
    ) {
      _placeholder
    }
  }
`

interface CreateRuleExclusionURLTypeArgs extends CommonArgs {
  readonly urls: string[]
}

export const createRuleExclusionURLType: ContrastGraphQLFieldConfig<
  any,
  Context,
  {
    readonly input: CreateRuleExclusionURLTypeArgs
  },
  GenericMutationResponse
> = {
  description: `${descriptionPrefix}: URL type`,
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: {
    input: {
      type: urlTypeArgs
    }
  },
  resolve: async (_source, args, { connector }) => {
    const {
      input: {
        organizationID,
        applicationID,
        suppressEvents,
        exclusionName,
        eventUUID,
        assessRulesInfo,
        protectRulesInfo,
        urls
      }
    } = args

    return sendRequests(
      connector,
      organizationID,
      applicationID,
      suppressEvents,
      'url',
      exclusionName,
      eventUUID,
      assessRulesInfo,
      protectRulesInfo,
      {
        urls
      }
    )
  }
}

interface CodeTypeBody {
  readonly codes: string[]
}

interface InputTypeBody {
  readonly input_type: InputType
  readonly input_name?: string
  readonly urls: string[]
}

interface URLTypeBody {
  readonly urls: string[]
}

const mapRulesInfoToBody = ({
  all,
  specificRuleUUIDs
}: CreateRuleExclusionRulesData) => ({
  all,
  specific_rule_uuids: specificRuleUUIDs
})

const sendRequests = async (
  connector: Connector,
  organizationID: string,
  applicationID: string,
  suppressEvents: boolean,
  urlSuffix: 'url' | 'code' | 'input',
  exclusionName: string,
  eventUUID: string,
  assessRulesInfo: CreateRuleExclusionRulesData,
  protectRulesInfo: CreateRuleExclusionRulesData,
  specificBody: CodeTypeBody | InputTypeBody | URLTypeBody
) => {
  const resp = await connector.post(
    `/${organizationID}/applications/${applicationID}/exclusions/${urlSuffix}`,
    {
      name: exclusionName,
      event_uuid: eventUUID,
      assess_rules_info: mapRulesInfoToBody(assessRulesInfo),
      protect_rules_info: mapRulesInfoToBody(protectRulesInfo),
      ...specificBody
    }
  )

  if (suppressEvents && resp.success && resp.exception) {
    await connector.put(
      `/${organizationID}/applications/${applicationID}/exclusions/${resp.exception.exception_id}/events`
    )
  }

  return { _placeholder: true }
}
